import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Filter } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { formatDate } from '../lib/format';
import { ListShell } from '../components/ListShell';
import { DataTable } from '../components/ui/DataTable';
import { Drawer } from '../components/ui/Drawer';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Pagination } from '../components/people/Pagination';
import { useDebouncedValue } from '../components/people/useDebouncedValue';
import { parseJsonField, type AuditLogRecord } from '../components/system/types';
import {
  FilterBar,
  SortableHeader,
  useListFilters,
  type FilterRoute,
} from '../components/filters';
import {
  auditFilterConfig,
  auditSearchSchema,
  AUDIT_SORT_COLUMNS,
} from '../components/system/auditFilterConfig';

const PAGE_SIZE = 20;

interface AuditListResponse {
  items: AuditLogRecord[];
  page: number;
  pageSize: number;
  total: number;
}

export const Route = createFileRoute('/_authed/system/audit')({
  validateSearch: auditSearchSchema,
  component: AuditLogPage,
});

const truncate = (value: string | null | undefined, max = 8): string => {
  if (!value) return '—';
  if (value.length <= max) return value;
  return `${value.slice(0, max)}…`;
};

function AuditLogPage() {
  const [selected, setSelected] = useState<AuditLogRecord | null>(null);

  // The single source of truth for search / filters / sort / page, backed by the
  // route's URL search params. The adapter narrows the route's precisely-typed
  // search/navigate surface to the toolkit's structural `FilterRoute` contract;
  // the route's own Zod `validateSearch` keeps the boundary typed.
  const filterRoute: FilterRoute = {
    useSearch: () => Route.useSearch() as Record<string, unknown>,
    useNavigate: () => {
      const navigateRoute = Route.useNavigate();
      return (opts) =>
        navigateRoute({
          search: ((prev: Record<string, unknown>) => opts.search(prev)) as never,
          ...(opts.replace !== undefined ? { replace: opts.replace } : {}),
        });
    },
  };
  const filters = useListFilters(auditFilterConfig, filterRoute, { pageSize: PAGE_SIZE });
  const { values, apiParams, setSort, setPage } = filters;

  // `actor` (an exact actor user-id match, `eq` on the backend) stays a SIBLING
  // text input — NOT a toolkit facet — exactly like the Withdrawals queue keeps
  // `academicYear` a sibling select. It lives in the same URL search contract
  // (`auditSearchSchema`). Because it is free text (not a fixed option set), the
  // write to the URL is debounced — mirroring the toolkit's own search facet —
  // so typing a uuid doesn't spam browser history or refetch per keystroke.
  const routeSearch = Route.useSearch();
  const routeNavigate = Route.useNavigate();
  const urlActor = routeSearch.actor ?? '';
  const [actorInput, setActorInput] = useState(urlActor);
  // Keep the local box in sync when the URL changes from elsewhere (back /
  // forward, a shared link) without clobbering what the user is mid-typing.
  const lastUrlActor = useRef(urlActor);
  useEffect(() => {
    if (urlActor !== lastUrlActor.current) {
      lastUrlActor.current = urlActor;
      setActorInput(urlActor);
    }
  }, [urlActor]);
  const debouncedActor = useDebouncedValue(actorInput.trim(), 300);
  useEffect(() => {
    if (debouncedActor === urlActor) return;
    lastUrlActor.current = debouncedActor;
    void routeNavigate({
      search: (prev) => ({
        ...prev,
        actor: debouncedActor || undefined,
        // Changing a filter resets pagination — mirror the toolkit's behaviour.
        page: undefined,
      }),
      replace: true,
    });
    // `urlActor` is intentionally excluded: this effect fires on the debounced
    // value settling, and the guard above short-circuits the no-op case.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedActor]);

  // The toolkit's date-range facet emits date-only `YYYY-MM-DD` bounds, but the
  // audit backend compares them RAW against ISO-datetime `occurredAt` values, so
  // a bare `to=2026-06-30` would exclude every event ON the 30th. Pad the bounds
  // to whole-day UTC here (route responsibility, per the auditFilterConfig note)
  // — the announcements backend pads server-side, so this is deliberately not in
  // the shared toolkit. Also fold in the sibling `actor` exact-match.
  const queryParams = useMemo(() => {
    const out: Record<string, string | number | undefined> = { ...apiParams };
    if (typeof out.from === 'string' && out.from) out.from = `${out.from}T00:00:00.000Z`;
    if (typeof out.to === 'string' && out.to) out.to = `${out.to}T23:59:59.999Z`;
    if (debouncedActor) out.actor = debouncedActor;
    return out;
  }, [apiParams, debouncedActor]);

  const listQuery = useQuery({
    queryKey: ['audit-logs', queryParams] as const,
    queryFn: ({ signal }) =>
      api.list<AuditLogRecord>('/api/audit-logs', queryParams, signal) as Promise<AuditListResponse>,
    placeholderData: (prev) => prev,
  });

  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const isLoadingList = listQuery.isLoading && !listQuery.data;

  const columns = useMemo<ColumnDef<AuditLogRecord, unknown>[]>(
    () => [
      {
        id: 'occurredAt',
        header: () => (
          <SortableHeader
            label="When"
            binding={AUDIT_SORT_COLUMNS.occurredAt}
            currentSort={values.sort}
            onSort={setSort}
          />
        ),
        cell: (info) => (
          <span className="text-xs text-[color:var(--color-text-muted)] tabular-nums">
            {formatDate(info.row.original.occurredAt, { withTime: true })}
          </span>
        ),
      },
      {
        id: 'actor',
        header: 'Actor',
        cell: (info) => {
          const r = info.row.original;
          return (
            <div className="min-w-0">
              <p className="text-xs font-medium capitalize text-[color:var(--color-text)]">
                {r.actorRole ? r.actorRole.replace('_', ' ') : 'system'}
              </p>
              <p className="font-mono text-[11px] text-[color:var(--color-text-muted)]">
                {truncate(r.actorId, 12)}
              </p>
            </div>
          );
        },
      },
      {
        id: 'action',
        header: 'Action',
        cell: (info) => (
          <span className="font-mono text-xs text-[color:var(--color-text)]">
            {info.row.original.action}
          </span>
        ),
      },
      {
        id: 'target',
        header: 'Target',
        cell: (info) => {
          const r = info.row.original;
          if (!r.targetType && !r.targetId) {
            return <span className="text-xs text-[color:var(--color-text-muted)]">—</span>;
          }
          return (
            <span className="font-mono text-xs text-[color:var(--color-text-muted)]">
              {r.targetType ?? 'unknown'}
              {r.targetId ? <span>:{truncate(r.targetId, 10)}</span> : null}
            </span>
          );
        },
      },
      {
        id: 'requestId',
        header: 'Request',
        cell: (info) => (
          <span className="font-mono text-[11px] text-[color:var(--color-text-muted)]">
            {truncate(info.row.original.requestId, 10)}
          </span>
        ),
      },
    ],
    [values.sort, setSort],
  );

  const hasFilters = filters.activeFilterCount > 0 || Boolean(debouncedActor);

  return (
    <ListShell
      title="Activity history"
      description="Every privileged action with actor, target, and timestamp."
    >
      <FilterBar config={auditFilterConfig} filters={filters} resultCount={total} />

      <div className="mb-4 max-w-xs">
        <label
          htmlFor="audit-actor"
          className="block text-xs font-medium text-[color:var(--color-text-muted)] mb-1"
        >
          Actor (user id)
        </label>
        <input
          id="audit-actor"
          type="search"
          value={actorInput}
          onChange={(e) => setActorInput(e.target.value)}
          placeholder="user uuid"
          aria-label="Filter by actor user id"
          className="w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 py-2 text-xs focus:border-gold-500 focus:outline-none focus:ring-1 focus:ring-gold-500"
        />
      </div>

      {listQuery.isError ? (
        <ErrorState
          title="Could not load the activity history"
          description={
            listQuery.error instanceof ApiError
              ? listQuery.error.message
              : 'Please try again in a moment.'
          }
          onRetry={() => listQuery.refetch()}
        />
      ) : (
        <>
          <DataTable<AuditLogRecord>
            data={items}
            columns={columns}
            isLoading={isLoadingList}
            getRowId={(row) => row.id}
            onRowClick={(row) => setSelected(row)}
            emptyState={
              <EmptyState
                title={hasFilters ? 'No entries match your filters' : 'No audit entries yet'}
                description={
                  hasFilters
                    ? 'Try clearing one of the filters above.'
                    : 'Privileged actions will appear here as they happen.'
                }
                icon={<Filter size={32} aria-hidden="true" />}
              />
            }
          />
          <Pagination
            page={values.page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={setPage}
          />
        </>
      )}

      <AuditDetailDrawer record={selected} onClose={() => setSelected(null)} />
    </ListShell>
  );
}

interface AuditDetailDrawerProps {
  record: AuditLogRecord | null;
  onClose: () => void;
}

function AuditDetailDrawer({ record, onClose }: AuditDetailDrawerProps) {
  const before = record ? parseJsonField<unknown>(record.before) : null;
  const after = record ? parseJsonField<unknown>(record.after) : null;
  const metadata = record ? parseJsonField<unknown>(record.metadata) : null;

  return (
    <Drawer open={record !== null} onClose={onClose} title="Activity detail" size="lg">
      {record ? (
        <div className="space-y-5 text-sm">
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FieldRow label="When" value={formatDate(record.occurredAt, { withTime: true })} />
            <FieldRow label="Action" value={<span className="font-mono">{record.action}</span>} />
            <FieldRow
              label="Actor role"
              value={
                <span className="capitalize">
                  {record.actorRole ? record.actorRole.replace('_', ' ') : 'system'}
                </span>
              }
            />
            <FieldRow
              label="Actor id"
              value={<span className="font-mono break-all">{record.actorId ?? '—'}</span>}
            />
            <FieldRow label="Target type" value={record.targetType ?? '—'} />
            <FieldRow
              label="Target id"
              value={<span className="font-mono break-all">{record.targetId ?? '—'}</span>}
            />
            <FieldRow
              label="Request id"
              value={<span className="font-mono break-all">{record.requestId ?? '—'}</span>}
            />
            <FieldRow label="Log id" value={<span className="font-mono break-all">{record.id}</span>} />
          </dl>

          <JsonBlock title="Before" value={before} />
          <JsonBlock title="After" value={after} />
          <JsonBlock title="Metadata" value={metadata} />
        </div>
      ) : null}
    </Drawer>
  );
}

function FieldRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {label}
      </dt>
      <dd className="mt-1 text-sm">{value}</dd>
    </div>
  );
}

function JsonBlock({ title, value }: { title: string; value: unknown }) {
  const isEmpty = value === null || value === undefined;
  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {title}
      </h3>
      <div className="mt-2 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
        {isEmpty ? (
          <p className="px-3 py-2 text-xs text-[color:var(--color-text-muted)]">
            No {title.toLowerCase()} captured.
          </p>
        ) : (
          <pre className="max-h-72 overflow-auto px-3 py-2 text-xs font-mono leading-relaxed text-[color:var(--color-text)]">
            {JSON.stringify(value, null, 2)}
          </pre>
        )}
      </div>
    </section>
  );
}
